import { Box, Card, CardHeader, Link, Stack, Typography } from "@mui/material";
import {
  DatagridConfigurable,
  Filter,
  FunctionField,
  List,
  SelectColumnsButton,
  TextField,
  TextInput,
  TopToolbar,
  useTranslate,
} from "react-admin";
import XCurrencyField from "src/pages/admin/components/XCurrencyField";
import { EmptyContent } from "src/components/empty-content";
import { CustomBreadcrumbs } from "src/components/custom-breadcrumbs";
import { DashboardContent } from "src/layouts/dashboard";
import { PermissionGuard } from "src/routes/components";
import ErrorPage403 from "src/pages/error/403";
import { Resources } from "../resource";
import FilterGroup from "../../components/FilterGroup";
import IdField from "../../components/IdField";
import { useState } from "react";

const CHANNELS_PREVIEW_COUNT = 1;

function ReferralCodeCell({ record }: { record: any }) {
  const [expanded, setExpanded] = useState(false);

  const ch: Array<{ channelName?: string; referralCode?: string }> = Array.isArray(record?.channels)
    ? record.channels
    : [];
  const lines = ch
    .map((c) => {
      const n = (c.channelName ?? "").trim();
      const r = (c.referralCode ?? "").trim();
      if (n && r) return `${r} (${n})`;
      return r || n || "";
    })
    .filter(Boolean);

  const code = record?.referralCode ?? "—";
  const hasChannels = lines.length > 0;
  const canExpand = lines.length > CHANNELS_PREVIEW_COUNT;
  const shown = expanded || !canExpand ? lines : lines.slice(0, CHANNELS_PREVIEW_COUNT);

  const cellSx = {
    whiteSpace: "normal" as const,
    wordBreak: "break-word" as const,
    maxWidth: 280,
    py: 0.5,
  };

  if (!hasChannels) {
    return (
      <Typography variant="body2" sx={cellSx}>
        {code}
      </Typography>
    );
  }

  return (
    <Stack spacing={0.25} sx={cellSx}>
      {shown.map((text, i) => (
        <Typography key={i} variant="body2" component="div">
          {text}
        </Typography>
      ))}

      {canExpand ? (
        <Link
          component="button"
          type="button"
          underline="none"
          sx={{ alignSelf: 'flex-start', fontSize: '0.75rem' }}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? '↑' : `↓ +${lines.length - CHANNELS_PREVIEW_COUNT}`}
        </Link>
      ) : null}
    </Stack>
  );
}

const ReferralRankingFilter = (props: any) => {
    const t = useTranslate();

    return (
      <div style={{ position: 'relative' }}>
        <Filter {...(props || {})}>
          <FilterGroup alwaysOn spacing={0} p={0}>
            <FilterGroup>
              <TextInput
                source="referralCode"
                label={
                  t('module.referralRanking.referralCode', { _: 'Referral Code' }) +
                  '/' +
                  t('module.referralRanking.bindedCode', { _: 'Binded Code' })
                }
              />
              <TextInput
                source="userId"
                label={t('module.referralRanking.userId', { _: 'User ID' })}
              />
            </FilterGroup>
          </FilterGroup>
        </Filter>
      </div>
    );
};

const ReferralRankingActions = () => (
  <TopToolbar>
    <SelectColumnsButton />
  </TopToolbar>
);

const ReferralRanking = () => {
    const t = useTranslate();

    return (
      <List
        resource="referral-ranking"
        exporter={false}
        perPage={25}
        empty={<EmptyContent />}
        sort={{ field: 'DOWNLINE_COUNT', order: 'DESC' }}
        filters={<ReferralRankingFilter />}
        actions={<ReferralRankingActions />}
      >
        <DatagridConfigurable
          bulkActionButtons={false}
          rowClick={false}
          preferenceKey="referral-ranking.datagrid"
          sx={{
            '& .MuiTableCell-root': {
              whiteSpace: 'nowrap',
            },
            /* Referral Code 列：纵向列出 channels，需允许换行 */
            '& tbody .MuiTableRow-root > .MuiTableCell-root:nth-of-type(2)': {
              whiteSpace: 'normal',
              verticalAlign: 'top',
            },
            '& th.referralCode, & td.referralCode': {
              minWidth: 220,
            },
            '& th.colPredictionVolume, & td.colPredictionVolume': {
              minWidth: 160,
            },
            '& th.colPredictionVolume': {
              whiteSpace: 'normal',
              lineHeight: 1.2,
            },
            overflow: 'auto',
          }}
        >
          <FunctionField
            label={t('module.referralRanking.id', { _: 'ID' })}
            sortable={false}
            render={(record: any) => <IdField id={record.userId} sx={{ width: '100px' }} />}
          />
          <TextField
            source="walletAddress"
            label={t('module.referralRanking.walletAddress', { _: 'Wallet Address' })}
            sortable={false}
          />
          <FunctionField
            source="referralCodes"
            label={t('module.referralRanking.referralCode', { _: 'Referral Code' })}
            sortable={false}
            render={(record: any) => {
              const codes: string[] = Array.isArray(record?.referralCodes) ? record.referralCodes : [];
              return (
                <Stack spacing={0.25}>
                  {codes.map((code, i) => (
                    <Typography key={i} variant="body2">{code}</Typography>
                  ))}
                </Stack>
              );
            }}
          />

          {/*<FunctionField*/}
          {/*  label={t('module.referralRanking.referralCode', { _: 'Referral Code' })}*/}
          {/*  sortable={false}*/}
          {/*  headerClassName="referralCode"*/}
          {/*  cellClassName="referralCode"*/}
          {/*  render={(record: any) => {*/}
          {/*    return <ReferralCodeCell record={record} />;*/}
          {/*  }}*/}
          {/*/>*/}
          <TextField
            source="bindedCode"
            label={t('module.referralRanking.bindedCode', { _: 'Binded Code' })}
            sortable={false}
          />
          <FunctionField
            label={t('module.referralRanking.level', { _: 'Level' })}
            sortable={false}
            render={(record: any) => (
              <Box
                component="span"
                sx={{
                  bgcolor: 'primary.lighter',
                  color: 'primary.dark',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                }}
              >
                {record.currentLevel}
              </Box>
            )}
          />

          <TextField
            source="teamSize"
            label={t('module.referralRanking.teamSize')}
            sortBy="DOWNLINE_COUNT"
          />
          <TextField
            source="directReferrals"
            label={t('module.referralRanking.directReferrals')}
            sortBy="DIRECT_COUNT"
          />

          <FunctionField
            label={t('module.referralRanking.predictionVolume', { _: 'Prediction Vol.' })}
            sortBy="PREDICTION_VOLUME"
            headerClassName="colPredictionVolume"
            cellClassName="colPredictionVolume"
            render={(record: any) => (
              <XCurrencyField
                value={record.predictionVolumeUsd}
                currency={{ symbol: '$', position: 'before' }}
              />
            )}
          />

          <FunctionField
            label={t('module.referralRanking.transactionVolume', { _: 'Transaction Vol.' })}
            sortBy="TRANSACTION_VOLUME"
            render={(record: any) => (
              <XCurrencyField
                value={record.transactionVolume}
                currency={{ symbol: '$', position: 'before' }}
              />
            )}
          />

          <FunctionField
            label={t('module.referralRanking.revenueGenerated', { _: 'Revenue Generated' })}
            sortBy="REVENUE_GENERATED"
            render={(record: any) => (
              <XCurrencyField
                value={record.revenueGenerated}
                currency={{ symbol: '$', position: 'before' }}
              />
            )}
          />

          <FunctionField
            label={t('module.referralRanking.totalCommission', { _: 'Total Commission' })}
            sortBy="COMMISSION_EARNED"
            render={(record: any) => (
              <XCurrencyField
                value={record.totalCommissionReceivedUsd}
                currency={{ symbol: '$', position: 'before' }}
              />
            )}
          />

          <FunctionField
            label={t('module.referralRanking.derivativesVolume', { _: 'Derivatives Vol.' })}
            sortBy="DERIVATIVES_VOLUME"
            render={(record: any) => (
              <XCurrencyField
                value={record.derivativesVolumeUsd}
                currency={{ symbol: '$', position: 'before' }}
              />
            )}
          />

          <FunctionField
            label={t('module.referralRanking.memeVolume', { _: 'Meme Vol.' })}
            sortBy="MEME_VOLUME"
            render={(record: any) => (
              <XCurrencyField
                value={record.memeVolumeUsd}
                currency={{ symbol: '$', position: 'before' }}
              />
            )}
          />
        </DatagridConfigurable>
      </List>
    );
}

const ReferralRankingList = () => {
    const t = useTranslate();

    return (
        <PermissionGuard resource="referral-ranking" action="read" fallback={<ErrorPage403 />}>
            <DashboardContent>
                <CustomBreadcrumbs
                    heading={t('module.referralRanking.title')}
                    links={[{ name: t('module.referralRanking.title'), href: `${Resources.referralRanking}` }]}
                />
                <Card sx={{ mt: 2 }}>
                    <ReferralRanking />
                </Card>
            </DashboardContent>
        </PermissionGuard>
    )
}

export default ReferralRankingList;
